// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, Tooltip, Typography, useMediaQuery } from '@mui/material';

// project imports
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import LogoSection from '../LogoSection';
import SearchSection from './SearchSection';
import ProfileSection from './ProfileSection';
import FullScreenSection from './FullScreenSection';
import BackendProcessControl from 'ui-component/backend-processes/BackendProcessControl';
import NotificationBell from 'ui-component/notifications/NotificationBell';

import { useDispatch, useSelector } from 'store';
import { openDrawer } from 'store/slices/menu';

// assets
import { IconClock, IconLock, IconMenu2 } from '@tabler/icons-react';
import useAuth from 'hooks/useAuth';

const Header = () => {
    const theme = useTheme();

    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);

    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
    const { layout } = useConfig();

    const { clientSettings } = useSelector((state) => state.settings);

    const { user } = useAuth();
    const isTemporarySupportAccess = user?.isAccessAdmin || false;

    return (
        <>
            {/* logo & toggler button */}
            <Box
                sx={{
                    width: 180,
                    display: 'flex',
                    alignItems: 'center',
                    [theme.breakpoints.down('md')]: {
                        width: 'auto'
                    }
                }}
            >
                <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <LogoSection />
                </Box>

                {layout === LAYOUT_CONST.VERTICAL_LAYOUT || (layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && matchDownMd) ? (
                    <Avatar
                        variant="rounded"
                        sx={{
                            ...theme.typography.commonAvatar,
                            ...theme.typography.mediumAvatar,
                            overflow: 'hidden',
                            transition: 'all .2s ease-in-out',
                            background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
                            color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
                            '&:hover': {
                                background: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
                                color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.light
                            }
                        }}
                        onClick={() => dispatch(openDrawer(!drawerOpen))}
                        color="inherit"
                    >
                        <IconMenu2 stroke={1.5} size="20px" />
                    </Avatar>
                ) : null}
            </Box>
            {/* header search */}
            {isTemporarySupportAccess && (
                <Box sx={{ display: { xs: 'none', xl: 'block' } }}>
                    <SearchSection />
                </Box>
            )}
            {isTemporarySupportAccess ? (
                <Tooltip arrow title="You are using temporary Access as Admin in restricted support mode. This session is read only.">
                    <Box
                        role="status"
                        tabIndex={0}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: { xs: 0.5, sm: 1 },
                            ml: { xs: 1, md: 2 },
                            mr: 1,
                            px: { xs: 1, sm: 1.5 },
                            py: 0.75,
                            minWidth: 0,
                            borderRadius: 1.5,
                            border: '1px solid #f0ce83',
                            bgcolor: '#fff7e6',
                            color: '#754b0b',
                            '&:focus-visible': { outline: '2px solid #f0ce83', outlineOffset: 2 }
                        }}
                    >
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexShrink: 0 }}>
                            <IconLock size={20} stroke={1.8} aria-hidden="true" />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                component="p"
                                className="main-font-b"
                                sx={{ fontSize: { xs: '0.6875rem', sm: '0.8125rem' }, lineHeight: 1.35, fontWeight: 600 }}
                            >
                                Temporary Support Access — Read Only
                            </Typography>
                            <Typography
                                component="p"
                                className="main-font"
                                sx={{ display: { xs: 'none', md: 'block' }, fontSize: '0.75rem', lineHeight: 1.4, mt: 0.25 }}
                            >
                                Restricted support session
                            </Typography>
                        </Box>
                    </Box>
                </Tooltip>
            ) : (
                <SearchSection />
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ flexGrow: 1 }} />

            {/* // trial identifier */}
            {clientSettings?.trial && clientSettings?.trialDaysRemaining >= 0 && (
                <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                    <Box sx={{ background: 'white', p: '4.8px 10px', borderRadius: 14, display: 'flex', alignItems: 'center' }}>
                        <IconClock size={20} color="#f3645a" />
                        <Typography variant="h3" sx={{ ml: 0.6 }} className="main-font-b" color="error">
                            {clientSettings?.trialDaysRemaining >= 0
                                ? clientSettings.trialDaysRemaining
                                : clientSettings.trialDaysRemaining}{' '}
                        </Typography>
                        <Typography variant="h3" className="main-font" color="#cfcfcf" sx={{ fontWeight: 100 }}>
                            |
                        </Typography>
                        <Box>
                            <Typography component="span" variant="subtitle2" className="main-font-b" color="error">
                                TRIAL
                            </Typography>
                            &nbsp;
                            <Typography variant="subtitle3" className="main-font" color="error">
                                days left
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}
            {/* full sceen toggler */}
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <FullScreenSection />
            </Box>
            <BackendProcessControl allowedPrincipalTypes={['user']} />
            <NotificationBell allowedPrincipalTypes={['user']} />
            {/* profile */}
            <ProfileSection />
        </>
    );
};

export default Header;
